import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Container,
    Paper,
    Typography,
    Button,
    Grid,
    TextField,
    Box,
    Avatar,
    Card,
    CardContent,
    Alert,
    Divider,
    IconButton,
    CircularProgress
} from '@mui/material';
import {
    Edit as EditIcon,
    Save as SaveIcon,
    Cancel as CancelIcon,
    Person as PersonIcon,
    Email as EmailIcon,
    Business as BusinessIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

const Profile = () => {
    const { mode } = useParams(); // 'edit' or undefined for view mode
    const navigate = useNavigate();
    const { user, updateUserInfo } = useAuth();
    const [isEditing, setIsEditing] = useState(mode === 'edit');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });

    useEffect(() => {
        if (user) {
            setFormData({
                firstName: user.firstName || '',
                lastName: user.lastName || '',
                email: user.email || '',
                currentPassword: '',
                newPassword: '',
                confirmPassword: ''
            });
        }
    }, [user]);

    useEffect(() => {
        setIsEditing(mode === 'edit');
    }, [mode]);

    const handleInputChange = (field) => (event) => {
        setFormData(prev => ({
            ...prev,
            [field]: event.target.value
        }));
        // Clear errors when user starts typing
        if (error) setError('');
        if (success) setSuccess('');
    };

    const handleKeyPress = (event) => {
        if (event.key === 'Enter' && isEditing && !loading) {
            event.preventDefault();
            handleSave();
        }
    };

    const hasUnsavedChanges = () => {
        if (!isEditing || !user) return false;
        return (
            (formData.firstName || '').trim() !== (user.firstName || '') ||
            (formData.lastName || '').trim() !== (user.lastName || '') ||
            (formData.email || '').trim() !== (user.email || '') ||
            (formData.currentPassword || '').trim() !== '' ||
            (formData.newPassword || '').trim() !== '' ||
            (formData.confirmPassword || '').trim() !== ''
        );
    };

    const handleSave = async () => {
        try {
            setLoading(true);
            setError('');

            // Validate required fields
            if (!formData.firstName || !formData.firstName.trim()) {
                setError('First name is required');
                return;
            }
            if (!formData.lastName || !formData.lastName.trim()) {
                setError('Last name is required');
                return;
            }
            if (!formData.email || !formData.email.trim()) {
                setError('Email is required');
                return;
            }
            
            // Basic email validation
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(formData.email.trim())) {
                setError('Please enter a valid email address');
                return;
            }

            // Validate passwords if changing password
            if (formData.newPassword && formData.newPassword.trim()) {
                if (!formData.currentPassword || !formData.currentPassword.trim()) {
                    setError('Current password is required to change password');
                    return;
                }
                if (formData.newPassword !== formData.confirmPassword) {
                    setError('New password and confirmation do not match');
                    return;
                }
                if (formData.newPassword.length < 6) {
                    setError('New password must be at least 6 characters long');
                    return;
                }
                
                // Add common password complexity validation
                const hasUpperCase = /[A-Z]/.test(formData.newPassword);
                const hasLowerCase = /[a-z]/.test(formData.newPassword);
                const hasNumbers = /\d/.test(formData.newPassword);
                const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(formData.newPassword);
                
                if (!hasUpperCase || !hasLowerCase || !hasNumbers || !hasSpecialChar) {
                    setError('Password must contain at least one uppercase letter, lowercase letter, number, and special character');
                    return;
                }
            }

            const updateData = {
                firstName: (formData.firstName || '').trim(),
                lastName: (formData.lastName || '').trim(),
                email: (formData.email || '').trim()
            };

            // Add password fields only if changing password (and they contain non-whitespace content)
            if (formData.newPassword && formData.newPassword.trim()) {
                updateData.currentPassword = formData.currentPassword || '';
                updateData.newPassword = formData.newPassword;
            }

            // Debug: Log the data being sent to the API
            console.log('🔍 Sending profile update data:', updateData);
            console.log('🔍 User object:', user);
            console.log('🔍 Form data before processing:', formData);
            console.log('🔍 Is changing password?', !!(formData.newPassword && formData.newPassword.trim()));
            
            // Log each field individually to check for issues
            console.log('🔍 Fields:', {
                firstName: `"${updateData.firstName}" (length: ${updateData.firstName.length})`,
                lastName: `"${updateData.lastName}" (length: ${updateData.lastName.length})`,
                email: `"${updateData.email}" (length: ${updateData.email.length})`,
                hasCurrentPassword: !!(updateData.currentPassword),
                hasNewPassword: !!(updateData.newPassword)
            });
            
            // Validate the data one more time before sending
            if (!updateData.firstName || updateData.firstName.length === 0) {
                setError('First name cannot be empty');
                return;
            }
            if (!updateData.lastName || updateData.lastName.length === 0) {
                setError('Last name cannot be empty');
                return;
            }
            if (!updateData.email || updateData.email.length === 0) {
                setError('Email cannot be empty');
                return;
            }
            
            // Call the backend API endpoint: PUT /api/auth/profile
            // Expected request format: { firstName, lastName, email, currentPassword?, newPassword? }
            // Expected response format: { message, user: UserInfo }
            const response = await api.put('/api/auth/profile', updateData);
            
            // Update user context with new info from the API response
            if (updateUserInfo && response.data?.user) {
                updateUserInfo(response.data.user);
            }

            // Show success message with additional context for password changes
            let successMessage = response.data?.message || 'Profile updated successfully!';
            if (updateData.newPassword) {
                successMessage += ' Your password has been changed.';
            }
            setSuccess(successMessage);
            
            // Clear password fields
            setFormData(prev => ({
                ...prev,
                currentPassword: '',
                newPassword: '',
                confirmPassword: ''
            }));

            // Navigate back to view mode
            setTimeout(() => {
                navigate('/profile');
            }, 1500);

        } catch (error) {
            console.error('Error updating profile:', error);
            console.error('🔍 Error response data:', error.response?.data);
            console.error('🔍 Error response status:', error.response?.status);
            console.error('🔍 Error response headers:', error.response?.headers);
            
            // Log the specific error details
            if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
                console.error('🔍 Specific errors:', error.response.data.errors);
                error.response.data.errors.forEach((err, index) => {
                    console.error(`🔍 Error ${index + 1}:`, err);
                });
            }
            
            let errorMessage = 'Failed to update profile';
            
            if (error.response?.data) {
                const errorData = error.response.data;
                
                // Handle the new structured error response format
                if (errorData.message && errorData.errors && Array.isArray(errorData.errors)) {
                    // Backend returns: { message: "...", errors: ["error1", "error2"] }
                    if (errorData.errors.length > 0) {
                        errorMessage = `${errorData.message}: ${errorData.errors.join('; ')}`;
                    } else {
                        errorMessage = errorData.message;
                    }
                } else if (errorData.message) {
                    // Simple message format: { message: "..." }
                    errorMessage = errorData.message;
                } else if (errorData.errors) {
                    // Handle legacy ASP.NET Core validation errors
                    if (Array.isArray(errorData.errors)) {
                        errorMessage = errorData.errors.map(err => err.description || err).join('; ');
                    } else if (typeof errorData.errors === 'object') {
                        const validationMessages = [];
                        for (const field in errorData.errors) {
                            const fieldErrors = errorData.errors[field];
                            if (Array.isArray(fieldErrors)) {
                                validationMessages.push(`${field}: ${fieldErrors.join(', ')}`);
                            }
                        }
                        if (validationMessages.length > 0) {
                            errorMessage = validationMessages.join('; ');
                        }
                    }
                } else if (errorData.error) {
                    // Handle exception error format: { error: "..." }
                    errorMessage = errorData.error;
                } else if (typeof errorData === 'string') {
                    errorMessage = errorData;
                }
            }
            
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        if (hasUnsavedChanges()) {
            const confirmDiscard = window.confirm('You have unsaved changes. Are you sure you want to discard them?');
            if (!confirmDiscard) return;
        }
        
        // Reset form data
        setFormData({
            firstName: user?.firstName || '',
            lastName: user?.lastName || '',
            email: user?.email || '',
            currentPassword: '',
            newPassword: '',
            confirmPassword: ''
        });
        setError('');
        setSuccess('');
        navigate('/profile');
    };

    const handleEdit = () => {
        navigate('/profile/edit');
    };

    return (
        <Container maxWidth="md">
            <Box sx={{ mt: 4, mb: 4 }}>
                <Paper elevation={3} sx={{ p: 4 }}>
                    {/* Header */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Avatar sx={{ mr: 3, width: 80, height: 80, bgcolor: 'primary.main', fontSize: '2rem' }}>
                                {user?.firstName?.charAt(0)}{user?.lastName?.charAt(0)}
                            </Avatar>
                            <Box>
                                <Typography variant="h4" gutterBottom>
                                    {isEditing ? 'Edit Profile' : 'My Profile'}
                                </Typography>
                                <Typography variant="subtitle1" color="text.secondary">
                                    {user?.tenantName}
                                </Typography>
                            </Box>
                        </Box>
                        {!isEditing && (
                            <IconButton 
                                color="primary" 
                                onClick={handleEdit}
                                size="large"
                                sx={{ bgcolor: 'primary.50' }}
                            >
                                <EditIcon />
                            </IconButton>
                        )}
                    </Box>

                    {/* Alerts */}
                    {error && (
                        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
                            {error}
                        </Alert>
                    )}
                    
                    {success && (
                        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
                            {success}
                        </Alert>
                    )}

                    {/* Profile Information */}
                    <Box component="form" onKeyPress={handleKeyPress}>
                        <Card variant="outlined" sx={{ mb: 3 }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                                    <PersonIcon sx={{ mr: 1 }} />
                                    Personal Information
                                </Typography>
                                <Grid container spacing={3}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="First Name"
                                        value={formData.firstName}
                                        onChange={handleInputChange('firstName')}
                                        disabled={!isEditing}
                                        required={isEditing}
                                        variant={isEditing ? "outlined" : "standard"}
                                        InputProps={{
                                            readOnly: !isEditing,
                                        }}
                                        error={isEditing && !formData.firstName.trim()}
                                        helperText={isEditing && !formData.firstName.trim() ? "First name is required" : ""}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Last Name"
                                        value={formData.lastName}
                                        onChange={handleInputChange('lastName')}
                                        disabled={!isEditing}
                                        required={isEditing}
                                        variant={isEditing ? "outlined" : "standard"}
                                        InputProps={{
                                            readOnly: !isEditing,
                                        }}
                                        error={isEditing && !formData.lastName.trim()}
                                        helperText={isEditing && !formData.lastName.trim() ? "Last name is required" : ""}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Email Address"
                                        type="email"
                                        value={formData.email}
                                        onChange={handleInputChange('email')}
                                        disabled={!isEditing}
                                        required={isEditing}
                                        variant={isEditing ? "outlined" : "standard"}
                                        InputProps={{
                                            readOnly: !isEditing,
                                            startAdornment: <EmailIcon sx={{ mr: 1, color: 'action.active' }} />,
                                        }}
                                        error={isEditing && (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))}
                                        helperText={isEditing && !formData.email.trim() ? "Email is required" : 
                                                   isEditing && formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email) ? "Please enter a valid email address" : ""}
                                    />
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>

                    {/* Account Information */}
                    <Card variant="outlined" sx={{ mb: 3 }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                                <BusinessIcon sx={{ mr: 1 }} />
                                Account Information
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        <strong>Organization:</strong> {user?.tenantName}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        <strong>Roles:</strong> {user?.roles?.join(', ') || 'None'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        <strong>Permissions:</strong> {user?.permissions?.length || 0} granted
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        <strong>Account Status:</strong> Active
                                    </Typography>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>

                    {/* Password Change Section (only in edit mode) */}
                    {isEditing && (
                        <Card variant="outlined" sx={{ mb: 3 }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Change Password (Optional)
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    Leave blank if you don't want to change your password
                                </Typography>
                                <Grid container spacing={3}>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Current Password"
                                            type="password"
                                            value={formData.currentPassword}
                                            onChange={handleInputChange('currentPassword')}
                                            error={formData.newPassword && formData.newPassword.trim() && (!formData.currentPassword || !formData.currentPassword.trim())}
                                            helperText={formData.newPassword && formData.newPassword.trim() && (!formData.currentPassword || !formData.currentPassword.trim()) ? "Current password is required to change password" : ""}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="New Password"
                                            type="password"
                                            value={formData.newPassword}
                                            onChange={handleInputChange('newPassword')}
                                            error={formData.newPassword && formData.newPassword.trim() && (
                                                formData.newPassword.length < 6 ||
                                                !/[A-Z]/.test(formData.newPassword) ||
                                                !/[a-z]/.test(formData.newPassword) ||
                                                !/\d/.test(formData.newPassword) ||
                                                !/[!@#$%^&*(),.?":{}|<>]/.test(formData.newPassword)
                                            )}
                                            helperText={
                                                formData.newPassword && formData.newPassword.trim() ? (
                                                    formData.newPassword.length < 6 ? "Must be at least 6 characters" :
                                                    !/[A-Z]/.test(formData.newPassword) ? "Must contain uppercase letter" :
                                                    !/[a-z]/.test(formData.newPassword) ? "Must contain lowercase letter" :
                                                    !/\d/.test(formData.newPassword) ? "Must contain a number" :
                                                    !/[!@#$%^&*(),.?":{}|<>]/.test(formData.newPassword) ? "Must contain special character" :
                                                    ""
                                                ) : "Must contain uppercase, lowercase, number, and special character"
                                            }
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Confirm New Password"
                                            type="password"
                                            value={formData.confirmPassword}
                                            onChange={handleInputChange('confirmPassword')}
                                            error={formData.confirmPassword && formData.confirmPassword.trim() && formData.newPassword !== formData.confirmPassword}
                                            helperText={formData.confirmPassword && formData.confirmPassword.trim() && formData.newPassword !== formData.confirmPassword ? "Passwords do not match" : ""}
                                        />
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    )}
                    </Box>

                    {/* Action Buttons */}
                    {isEditing && (
                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                            <Button
                                variant="outlined"
                                startIcon={<CancelIcon />}
                                onClick={handleCancel}
                                disabled={loading}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                                onClick={handleSave}
                                disabled={loading}
                            >
                                {loading ? 'Saving...' : 'Save Changes'}
                            </Button>
                        </Box>
                    )}
                </Paper>
            </Box>
        </Container>
    );
};

export default Profile;